import '@fontsource/barlow/400.css';
import '@fontsource/barlow/500.css';
import '@fontsource/barlow/600.css';
import '@fontsource/barlow/700.css';
import '@fontsource/barlow-condensed/600.css';
import '@fontsource/barlow-condensed/700.css';
import '@fontsource/barlow-condensed/800.css';
import '@fontsource/barlow-condensed/800-italic.css';
import { canUseGuestReviewBridge } from './testing/reviewBridgeAccess';
import { getOAuthErrorMessage, showOAuthErrorBanner } from './auth/oauthFeedback';
import { GAME_CONTAINER_ID, renderBootstrapFailure } from './bootstrapFailure';
import { clientErrorReporter, describeErrorReason } from './net/clientErrorReporter';

// First statement on the page: without these listeners an alpha tester's crash leaves no trace at all.
clientErrorReporter.install();

// Handle OAuth error parameter. Successful OAuth now relies on HttpOnly cookies.
function handleOAuthCallback() {
  const message = getOAuthErrorMessage(window.location.search);
  if (message) {
    window.history.replaceState({}, '', window.location.pathname);
    showOAuthErrorBanner(document, message);
  }
}

interface ReviewSession {
  valid?: boolean;
  role?: string;
}

async function canInstallE2EBridge(params: URLSearchParams): Promise<boolean> {
  if (params.get('e2e') !== '1') return false;

  // Local and CI captures use an explicit build-time opt-in. Production never does: its bridge
  // is available only to an authenticated admin review session, so ordinary players cannot load
  // debug helpers through a query parameter.
  if (import.meta.env.VITE_E2E_BRIDGE) return true;

  try {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${apiUrl}/api/auth/verify`, { credentials: 'include' });
    if (!response.ok) return false;
    const session = await response.json() as ReviewSession;
    return session.valid === true && session.role === 'admin';
  } catch {
    return false;
  }
}

async function canInstallGuestReviewBridge(params: URLSearchParams): Promise<boolean> {
  if (params.get('review') !== 'guest') return false;

  // A public review must start before the anonymous bootstrap creates a guest account.
  // Existing player or admin sessions never receive this offline-only bridge.
  try {
    const apiUrl = import.meta.env.VITE_API_URL || '';
    const response = await fetch(`${apiUrl}/api/auth/verify`, { credentials: 'include' });
    return canUseGuestReviewBridge(params, response.status);
  } catch {
    return canUseGuestReviewBridge(params, null);
  }
}

// Initialize the game once the DOM is ready. Module evaluation can occur after
// DOMContentLoaded in browser automation or cached production loads, so start
// immediately in that case rather than leaving the shell without a game.
async function bootstrapGame() {
  handleOAuthCallback();

  const { gameDisplaySettingsState } = await import('./state/gameDisplaySettings');
  gameDisplaySettingsState.apply();

  // Install before Phaser loads so unsupported viewports are gated immediately.
  const { installViewportGate, labelGameCanvas } = await import('./game/viewportGate');
  installViewportGate();

  const params = new URLSearchParams(window.location.search);
  const installE2E = await canInstallE2EBridge(params);
  // Check before constructing Phaser: BootScene creates a guest account for a fresh visitor.
  // The guest-review bridge must make its authorization decision before that bootstrap begins.
  const installGuestReview = !installE2E && await canInstallGuestReviewBridge(params);

  // Lazy-load Phaser game bootstrap to keep initial entry chunk small.
  const { Game } = await import('./game/Game');
  const game = new Game();

  const CANVAS_LABEL =
    'Tower Power game screen. A medieval tower defense game rendered on a canvas; it is played with a mouse or touch and is not currently screen-reader navigable.';

  // Phaser boots synchronously when the document is already interactive, so the canvas
  // usually exists here. Fall back to 'ready' for the deferred-boot path.
  if (game.canvas) {
    labelGameCanvas(game.canvas, CANVAS_LABEL);
  } else {
    game.events.once('ready', () => labelGameCanvas(game.canvas, CANVAS_LABEL));
  }

  if (installE2E) {
    const { installE2EBridge } = await import('./testing/e2eBridge');
    installE2EBridge(game);
  } else if (installGuestReview) {
    const { installGuestReviewBridge } = await import('./testing/guestReviewBridge');
    installGuestReviewBridge(game);
  }
}

// A throw anywhere in the bootstrap used to leave the shell blank with nothing on the page and
// nothing in the logs; report it and say so where the canvas would have been.
function failBootstrap(error: unknown): void {
  const { message, stack } = describeErrorReason(error);
  clientErrorReporter.report('bootstrap', message, stack);
  renderBootstrapFailure({ container: document.getElementById(GAME_CONTAINER_ID) });
}

async function startGame(): Promise<void> {
  try {
    await bootstrapGame();
  } catch (error) {
    failBootstrap(error);
  }
}

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', () => void startGame(), { once: true });
} else {
  void startGame();
}
